import {
  STREAMLAIN_APP_TOPBAR_HEIGHT,
  STREAMLAIN_COMPACT_HEADER_PADDING,
} from '@blocksuite/affine-shared/styles';
import { css } from '@emotion/css';

import {
  emptyStateCtaStyle,
  emptyStateHintStyle,
  emptyStateStyle,
  emptyStateTitleStyle,
} from '../../shared/empty-state/empty-state-css.js';
import { slimDataViewScrollbarStyle } from '../../shared/slim-scrollbar-style.js';
import {
  temporalToolbarButtonStyle,
  temporalToolbarGroupStyle,
  temporalToolbarIconButtonStyle,
  temporalToolbarPrimaryButtonStyle,
  temporalToolbarRangeGroupStyle,
  temporalToolbarRangeTitleStyle,
} from '../../shared/toolbar/temporal-toolbar-css.js';
import {
  CALENDAR_SURFACE_TIME_AXIS_WIDTH,
  CALENDAR_SURFACE_WEEK_DAY_MIN_WIDTH,
} from './calendar-surface-range.js';

const CALENDAR_WEEK_HEADER_HEIGHT = 58;
const CALENDAR_WEEK_ALL_DAY_HEIGHT = 44;
const CALENDAR_WEEK_HOUR_HEIGHT = 48;
const CALENDAR_WEEK_HOUR_COUNT = 24;
const CALENDAR_WEEK_HOUR_GRID_HEIGHT =
  CALENDAR_WEEK_HOUR_HEIGHT * CALENDAR_WEEK_HOUR_COUNT;
const CALENDAR_WEEK_SURFACE_MIN_HEIGHT =
  CALENDAR_WEEK_HEADER_HEIGHT +
  CALENDAR_WEEK_ALL_DAY_HEIGHT +
  CALENDAR_WEEK_HOUR_GRID_HEIGHT;
const calendarStickyColumnSolidEdge = {
  boxShadow:
    '-2px 0 0 var(--streamlain-calendar-sticky-surface-background, var(--streamlain-v2-layer-background-primary)), inset 2px 0 0 var(--streamlain-calendar-sticky-surface-background, var(--streamlain-v2-layer-background-primary))',
  boxSizing: 'border-box',
  overflow: 'hidden',
  width: '56px',
} as const;

export const styles = {
  actionGroup: temporalToolbarGroupStyle,
  calendarDocMode: css({
    minHeight: '360px',
    width: '100%',
  }),
  calendarEdgelessMode: css({
    contain: 'layout style',
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'visible',
    width: '100%',
    '& > div': {
      flex: '0 0 auto',
      minHeight: 0,
    },
  }),
  calendarFullscreenMode: css({
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
    '& > [data-data-view-body="true"]': {
      display: 'flex',
      flex: '1 1 auto',
      height: '100%',
      gap: 0,
      minHeight: 0,
      overflow: 'hidden',
    },
    '& [data-data-view-body-content="true"]': {
      flex: '1 1 auto',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    },
    '& [data-data-view-body-intrinsic="true"][data-has-details]': {
      flex: '1 1 auto',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    },
    '& [data-calendar-surface="true"]': {
      flex: '1 1 auto',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    },
    '& [data-data-view-body-render-root="true"]': {
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
    },
    '& [data-calendar-week-scroll]': {
      flex: '1 1 auto',
      height: '100%',
      overflowY: 'auto',
      overscrollBehavior: 'none',
    },
  }),
  calendarHost: css({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    position: 'relative',
    userSelect: 'none',
  }),
  calendarSetupWizardInline: css({
    display: 'block',
    flex: '0 0 auto',
    width: '100%',
  }),
  calendar: css({
    background: 'var(--streamlain-v2-layer-background-primary)',
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    gap: '8px',
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
  }),
  cellAddButton: css({
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'var(--streamlain-v2-text-secondary)',
    cursor: 'pointer',
    display: 'inline-flex',
    height: '20px',
    justifyContent: 'center',
    width: '20px',
    ':hover': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
  }),
  cellDate: css({
    alignItems: 'center',
    color: 'var(--streamlain-v2-text-secondary)',
    display: 'flex',
    fontSize: '12px',
    fontWeight: 600,
    justifyContent: 'space-between',
    lineHeight: '20px',
    padding: '6px 6px 4px',
    '& strong': {
      alignItems: 'center',
      display: 'inline-flex',
      height: '24px',
      justifyContent: 'center',
      width: '24px',
    },
  }),
  checkboxRow: css({
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    fontSize: '13px',
  }),
  attendeeChip: css({
    alignItems: 'center',
    background: 'var(--streamlain-v2-layer-background-secondary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '999px',
    color: 'var(--streamlain-v2-text-primary)',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: '12px',
    gap: '6px',
    minHeight: '24px',
    padding: '0 8px',
    '& small': {
      color: 'var(--streamlain-v2-text-secondary)',
      fontSize: '11px',
    },
    '&[data-attendee-status="accepted"]': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-status-success) 12%, var(--streamlain-v2-layer-background-primary))',
      borderColor:
        'color-mix(in srgb, var(--streamlain-v2-status-success) 36%, var(--streamlain-v2-layer-insideBorder-border))',
    },
    '&[data-attendee-status="tentative"]': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-status-warning) 13%, var(--streamlain-v2-layer-background-primary))',
      borderColor:
        'color-mix(in srgb, var(--streamlain-v2-status-warning) 38%, var(--streamlain-v2-layer-insideBorder-border))',
    },
    '&[data-attendee-status="declined"], &[data-attendee-status="removed"]': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-status-error) 10%, var(--streamlain-v2-layer-background-primary))',
      borderColor:
        'color-mix(in srgb, var(--streamlain-v2-status-error) 34%, var(--streamlain-v2-layer-insideBorder-border))',
    },
  }),
  chipList: css({
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    minHeight: '26px',
    '& small': {
      color: 'var(--streamlain-v2-text-secondary)',
      fontStyle: 'italic',
    },
  }),
  content: css({
    display: 'flex',
    flex: '0 0 auto',
    position: 'relative',
    minHeight: 0,
    minWidth: 0,
    '& > [data-calendar-surface]': { flex: '1 1 auto', minWidth: 0 },
    '&[data-calendar-detail-only="true"]': { flex: '1 1 auto', width: '100%' },
    '&[data-calendar-detail-only="true"] > *': {
      width: '100%',
      flex: '1 1 auto',
    },
  }),
  conflict: css({
    background: 'var(--streamlain-v2-layer-background-error)',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--streamlain-v2-status-error)',
    fontSize: '12px',
    fontWeight: 510,
    padding: '8px 12px',
  }),
  configuredBody: css({
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    gap: '8px',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    '&[data-calendar-detail-only="true"]': {
      flex: '1 1 auto',
    },
  }),
  configuredToolbar: css({
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    gap: '8px',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
  }),
  dayHeader: css({
    alignItems: 'center',
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    border: 'none',
    borderBottom: '2px solid transparent',
    borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: 0,
    boxSizing: 'border-box',
    color: 'var(--streamlain-v2-text-primary)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    justifyContent: 'center',
    minHeight: '58px',
    minWidth: 0,
    overflow: 'visible',
    padding: '6px 4px 5px',
    position: 'relative',
    '& span': {
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: '18px',
    },
    '& strong': {
      alignItems: 'center',
      display: 'inline-flex',
      fontSize: '18px',
      fontWeight: 600,
      height: '28px',
      justifyContent: 'center',
      lineHeight: '28px',
      width: '28px',
    },
    '&[data-today="true"]': {
      borderBottomColor: 'var(--streamlain-v2-brand-primary)',
      color: 'var(--streamlain-v2-brand-primary)',
      '& strong': {
        background: 'var(--streamlain-v2-brand-primary)',
        borderRadius: '999px',
        color: 'var(--streamlain-pure-white)',
      },
    },
  }),
  dayHeaders: css({
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    border: 'none',
    borderBottom: 'none',
    borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: 0,
    display: 'grid',
    gridAutoRows: '58px',
    gridTemplateColumns:
      'repeat(var(--calendar-day-count, 7), minmax(var(--calendar-day-width, 0px), 1fr))',
    overflow: 'visible',
    position: 'relative',
    // Drag ghosts stay above calendar events, but never above the table head.
    zIndex: 30,
    '&[data-time-axis="true"]': {
      gridTemplateColumns:
        '56px repeat(var(--calendar-day-count, 7), minmax(var(--calendar-day-width, 0px), 1fr))',
    },
  }),
  dayHeaderSpacer: css({
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    left: 0,
    position: 'sticky',
    zIndex: 3,
    ...calendarStickyColumnSolidEdge,
  }),
  dayMode: css({
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minHeight: '420px',
    minWidth: 0,
  }),
  details: css({
    background:
      'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 96%, var(--streamlain-v2-layer-background-secondary))',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: 0,
    boxShadow: 'var(--streamlain-v2-shadow-level1)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
  }),
  detailsBody: css({
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: '12px',
    height: 0,
    minHeight: 0,
    overflow: 'auto',
    overscrollBehavior: 'none',
    padding: '12px',
    ...slimDataViewScrollbarStyle,
  }),
  detailsHeader: css({
    alignItems: 'center',
    background:
      'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 96%, var(--streamlain-v2-layer-background-secondary))',
    borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '46px',
    padding: '10px 12px',
    '& strong': {
      color: 'var(--streamlain-v2-text-primary)',
      fontSize: '15px',
      fontWeight: 600,
      lineHeight: '22px',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
  detailsActions: css({
    alignItems: 'center',
    background:
      'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 96%, var(--streamlain-v2-layer-background-secondary))',
    borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '10px 12px',
  }),
  dangerButton: css({
    alignItems: 'center',
    background:
      'color-mix(in srgb, var(--streamlain-v2-status-error) 10%, transparent)',
    border:
      '1px solid color-mix(in srgb, var(--streamlain-v2-status-error) 44%, transparent)',
    borderRadius: '6px',
    color: 'var(--streamlain-v2-status-error)',
    cursor: 'pointer',
    display: 'inline-flex',
    height: '30px',
    justifyContent: 'center',
    marginLeft: 'auto',
    padding: '0 10px',
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  }),
  eventBadge: css({
    color: 'var(--streamlain-v2-text-secondary)',
    fontSize: '11px',
  }),
  eventChip: css({
    alignItems: 'center',
    appearance: 'none',
    background:
      'color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 14%, var(--streamlain-v2-layer-background-primary))',
    border: '1px solid transparent',
    borderLeft:
      '4px solid var(--calendar-event-color, var(--streamlain-v2-brand-primary))',
    borderRadius: '5px',
    color: 'var(--streamlain-v2-text-primary)',
    cursor: 'grab',
    display: 'grid',
    font: 'inherit',
    gap: '2px',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    minHeight: '28px',
    minWidth: 0,
    overflow: 'hidden',
    padding: '4px 6px',
    position: 'relative',
    textAlign: 'left',
    '&[data-all-day="true"]': {
      background:
        'color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 18%, transparent)',
    },
    '&[data-selected="true"]': {
      borderColor: 'var(--streamlain-v2-brand-primary)',
    },
    '&[data-dragging="true"]': {
      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.16)',
      cursor: 'grabbing',
      opacity: 0.94,
      pointerEvents: 'none',
      zIndex: 8,
    },
    '&[data-calendar-drag-placeholder="true"]': {
      opacity: 0.34,
      pointerEvents: 'none',
    },
    '&[data-calendar-drag-ghost="true"]': {
      boxShadow: '0 12px 24px rgba(15, 23, 42, 0.22)',
      opacity: 0.96,
      pointerEvents: 'none',
      position: 'relative',
      zIndex: 24,
    },
    '&[data-calendar-drag-ghost="true"][data-calendar-drag-floating="true"]': {
      transform:
        'translate(var(--calendar-drag-x, 0px), var(--calendar-drag-y, 0px)) scale(1.01)',
      willChange: 'transform',
    },
    '&[data-invalid="true"]': {
      borderColor: 'var(--streamlain-v2-status-error)',
      borderLeftColor: 'var(--streamlain-v2-status-error)',
    },
    '&[data-invitation-preview="true"]': {
      background:
        'color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 6%, var(--streamlain-v2-layer-background-primary))',
      borderColor:
        'color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 48%, var(--streamlain-v2-layer-insideBorder-border))',
      borderStyle: 'dashed',
      borderLeftStyle: 'solid',
      opacity: 0.78,
    },
    ':active': {
      cursor: 'grabbing',
    },
    '&[data-readonly="true"]:not([data-invitation-preview="true"])': {
      cursor: 'pointer',
    },
  }),
  eventStack: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '0 6px 6px',
  }),
  eventTime: css({
    color: 'var(--streamlain-v2-text-secondary)',
    fontSize: '11px',
    whiteSpace: 'nowrap',
  }),
  eventMetadata: css({
    color: 'var(--streamlain-v2-text-secondary)',
    display: 'flex',
    fontSize: '10px',
    gap: '4px',
    gridColumn: '1 / -1',
    minWidth: 0,
    overflow: 'hidden',
    '& span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& span + span::before': {
      content: '"·"',
      marginInlineEnd: '4px',
    },
  }),
  eventTitle: css({
    borderRadius: '3px',
    color: 'var(--streamlain-v2-text-primary)',
    cursor: 'text',
    fontSize: '12px',
    overflow: 'hidden',
    outline: 'none',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&[contenteditable="plaintext-only"]:focus': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 72%, transparent)',
      boxShadow: '0 0 0 1px var(--streamlain-v2-brand-primary)',
      paddingInline: '2px',
    },
  }),
  feedField: css({
    alignItems: 'center',
    display: 'grid',
    gap: '8px',
    gridTemplateColumns: 'auto minmax(180px, 360px)',
    minWidth: 0,
    '& span': {
      color: 'var(--streamlain-v2-text-secondary)',
      fontSize: '12px',
    },
    '& input': {
      background: 'var(--streamlain-v2-layer-background-primary)',
      border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
      borderRadius: '6px',
      color: 'var(--streamlain-v2-text-primary)',
      font: 'inherit',
      height: '30px',
      minWidth: 0,
      padding: '0 8px',
    },
  }),
  feedForm: css({
    alignItems: 'center',
    background: 'var(--streamlain-v2-layer-background-secondary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '8px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '8px',
  }),
  field: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '12px',
    color: 'var(--streamlain-v2-text-secondary)',
    '& > span': {
      color: 'var(--streamlain-v2-text-secondary)',
      fontWeight: 500,
    },
    '& input, & textarea, & select': {
      background: 'var(--streamlain-v2-layer-background-primary)',
      border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
      borderRadius: '6px',
      boxSizing: 'border-box',
      color: 'var(--streamlain-v2-text-primary)',
      font: 'inherit',
      minHeight: '34px',
      minWidth: 0,
      padding: '0 8px',
      width: '100%',
    },
    '& input:focus, & textarea:focus, & select:focus': {
      borderColor: 'var(--streamlain-v2-brand-primary)',
      outline: 'none',
      boxShadow:
        '0 0 0 2px color-mix(in srgb, var(--streamlain-v2-brand-primary) 18%, transparent)',
    },
    '& input:disabled, & textarea:disabled, & select:disabled': {
      cursor: 'not-allowed',
      opacity: 0.64,
    },
    '& textarea': {
      lineHeight: '18px',
      minHeight: '86px',
      padding: '8px',
      resize: 'vertical',
    },
  }),
  sectionDivider: css({
    borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    marginTop: '4px',
    paddingTop: '12px',
  }),
  advancedSection: css({
    borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    marginTop: '12px',
    paddingTop: '10px',
    '& summary': {
      alignItems: 'center',
      color: 'var(--streamlain-v2-text-secondary)',
      cursor: 'pointer',
      display: 'flex',
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: '20px',
      listStyle: 'none',
    },
    '& summary::-webkit-details-marker': {
      display: 'none',
    },
    '& summary::before': {
      content: '"›"',
      display: 'inline-block',
      marginRight: '6px',
      transform: 'rotate(0deg)',
    },
    '&[open] summary::before': {
      transform: 'rotate(90deg)',
    },
  }),
  advancedSectionBody: css({
    paddingTop: '10px',
  }),
  fileInput: css({
    display: 'none',
  }),
  formGrid: css({
    display: 'grid',
    gap: '8px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  }),
  iconButton: temporalToolbarIconButtonStyle,
  inlineIconButton: css({
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'var(--streamlain-v2-text-secondary)',
    cursor: 'pointer',
    display: 'inline-flex',
    height: '22px',
    justifyContent: 'center',
    width: '22px',
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
    ':hover': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
  }),
  inlineTextButton: css({
    background: 'transparent',
    border: 'none',
    color: 'var(--streamlain-v2-brand-primary)',
    cursor: 'pointer',
    font: 'inherit',
    padding: '0 0 0 6px',
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  }),
  inlineEditorRow: css({
    alignItems: 'center',
    display: 'grid',
    gap: '8px',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    '& input, & select': {
      background: 'var(--streamlain-v2-layer-background-primary)',
      border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
      borderRadius: '6px',
      color: 'var(--streamlain-v2-text-primary)',
      font: 'inherit',
      height: '30px',
      minWidth: 0,
      padding: '0 8px',
    },
  }),
  modeButton: temporalToolbarButtonStyle,
  modeGroup: css(temporalToolbarGroupStyle, {
    flexWrap: 'nowrap',
    flex: 'none',
  }),
  leadingGroup: css({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  }),
  moreMenu: css({
    display: 'inline-flex',
    position: 'relative',
    '& summary': {
      listStyle: 'none',
    },
    '& summary::-webkit-details-marker': {
      display: 'none',
    },
  }),
  monthCell: css({
    background: 'var(--streamlain-v2-layer-background-primary)',
    borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    minHeight: '122px',
    minWidth: 0,
    '&[data-outside-month="true"]': {
      opacity: 0.55,
    },
    '&[data-today="true"]': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-brand-primary) 6%, transparent)',
      boxShadow: 'inset 0 0 0 1px var(--streamlain-v2-brand-primary)',
    },
  }),
  monthGrid: css({
    borderLeft: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: 0,
    display: 'grid',
    gridTemplateColumns: '34px repeat(7, minmax(0, 1fr))',
    overflow: 'visible',
  }),
  monthWeekButton: css({
    alignItems: 'center',
    appearance: 'none',
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    border: 'none',
    borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    color: 'var(--streamlain-v2-text-secondary)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    font: 'inherit',
    gap: '1px',
    justifyContent: 'center',
    minHeight: '122px',
    minWidth: 0,
    padding: '4px 2px',
    position: 'sticky',
    left: 0,
    zIndex: 2,
    '& small': {
      fontSize: '9px',
      lineHeight: 1,
    },
    '& strong': {
      color: 'var(--streamlain-v2-text-primary)',
      fontSize: '11px',
      lineHeight: 1.1,
    },
    ':hover': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
      color: 'var(--streamlain-v2-brand-primary)',
    },
    ':focus-visible': {
      outline: '2px solid var(--streamlain-v2-brand-primary)',
      outlineOffset: '-2px',
    },
  }),
  monthScroll: css({
    flex: '1 1 auto',
    minHeight: '360px',
    minWidth: 0,
    overflow: 'auto',
    overscrollBehavior: 'none',
    position: 'relative',
  }),
  moreEventsButton: css({
    background: 'transparent',
    border: 'none',
    borderRadius: '5px',
    color: 'var(--streamlain-v2-brand-primary)',
    cursor: 'pointer',
    fontSize: '12px',
    height: '26px',
    padding: '0 6px',
    textAlign: 'left',
    ':hover': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
  }),
  navGroup: temporalToolbarRangeGroupStyle,
  notice: css({
    background: 'var(--streamlain-v2-layer-background-secondary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '6px',
    color: 'var(--streamlain-v2-text-secondary)',
    fontSize: '12px',
    padding: '8px',
  }),
  overlayBar: css({
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '8px',
  }),
  overlayToggle: css({
    alignItems: 'center',
    background: 'var(--streamlain-v2-layer-background-secondary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '6px',
    color: 'var(--streamlain-v2-text-secondary)',
    display: 'inline-flex',
    fontSize: '12px',
    gap: '6px',
    minHeight: '28px',
    padding: '0 8px',
  }),
  primaryButton: temporalToolbarPrimaryButtonStyle,
  menuPanel: css({
    background: 'var(--streamlain-v2-layer-background-primary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '8px',
    boxShadow: 'var(--streamlain-v2-shadow-level2)',
    boxSizing: 'border-box',
    width: '244px',
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'min(640px, calc(100dvh - 100px))',
    overflowY: 'auto',
    padding: '6px',
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 6px)',
    zIndex: 30,
  }),
  menuSource: css({
    padding: '4px 4px 8px',
    borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  }),
  sourceSelectButton: css({
    alignItems: 'center',
    background: 'var(--streamlain-v2-layer-background-primary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '4px',
    color: 'var(--streamlain-v2-text-primary)',
    cursor: 'pointer',
    display: 'flex',
    font: 'inherit',
    gap: '8px',
    justifyContent: 'space-between',
    minHeight: '30px',
    padding: '4px 6px',
    width: '100%',
    '&:hover:not(:disabled)': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
    '&:focus-visible': {
      outline: '2px solid var(--streamlain-v2-brand-primary)',
    },
    '&:disabled': { opacity: 0.55, cursor: 'default' },
    '& > span:first-child': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& svg': { width: '16px', height: '16px', flexShrink: 0 },
  }),
  menuGroup: css({
    display: 'grid',
    gap: '2px',
    padding: '4px 0',
    '& + &': {
      borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    },
  }),
  menuItem: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
    width: '100%',
    minHeight: '30px',
    border: 0,
    borderRadius: '4px',
    background: 'transparent',
    color: 'var(--streamlain-v2-text-primary)',
    font: 'inherit',
    fontSize: '12px',
    padding: '6px 8px',
    textAlign: 'start',
    cursor: 'pointer',
    '&:hover:not(:disabled), &:focus-visible': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
    '&:focus-visible': {
      outline: '2px solid var(--streamlain-v2-brand-primary)',
      outlineOffset: '-2px',
    },
    '&:disabled': {
      color: 'var(--streamlain-v2-text-secondary)',
      opacity: 0.55,
      cursor: 'default',
    },
  }),
  menuField: css({
    alignItems: 'center',
    color: 'var(--streamlain-v2-text-primary)',
    display: 'flex',
    fontSize: '12px',
    gap: '4px',
    minHeight: '30px',
    padding: '4px 6px',
    '& > label': {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      flex: 1,
      gap: '8px',
    },
    '& input[type="checkbox"]': {
      appearance: 'auto',
      accentColor: 'var(--streamlain-v2-brand-primary)',
      cursor: 'pointer',
      height: '16px',
      width: '16px',
      margin: 0,
      flex: '0 0 16px',
    },
    '& > button': { flexShrink: 0 },
  }),
  mailAccountList: css({
    display: 'grid',
    gap: '4px',
  }),
  mailAccountOption: css({
    alignItems: 'center',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: '6px',
    color: 'var(--streamlain-v2-text-primary)',
    cursor: 'pointer',
    display: 'flex',
    font: 'inherit',
    justifyContent: 'space-between',
    minHeight: '30px',
    padding: '0 8px',
    textAlign: 'left',
    '&[data-active="true"]': {
      background: 'var(--streamlain-v2-layer-background-secondary)',
      borderColor: 'var(--streamlain-v2-layer-insideBorder-border)',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  }),
  rangeTitle: temporalToolbarRangeTitleStyle,
  rangeLoading: css({
    alignItems: 'center',
    background: 'var(--streamlain-v2-layer-background-secondary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '999px',
    boxShadow: 'var(--streamlain-v2-shadow-level1)',
    color: 'var(--streamlain-v2-text-secondary)',
    display: 'inline-flex',
    fontSize: '12px',
    gap: '6px',
    left: '50%',
    minHeight: '28px',
    padding: '0 12px',
    pointerEvents: 'none',
    position: 'absolute',
    top: '10px',
    transform: 'translateX(-50%)',
    width: 'max-content',
    zIndex: 20,
    '&[data-direction="previous"]': {
      left: '10px',
      transform: 'none',
    },
    '&[data-direction="next"]': {
      left: 'auto',
      right: '10px',
      transform: 'none',
    },
    '&[data-mode="month"][data-direction="next"]': {
      bottom: '10px',
      top: 'auto',
    },
    '&::before': {
      border: '2px solid var(--streamlain-v2-layer-insideBorder-border)',
      borderTopColor: 'var(--streamlain-v2-brand-primary)',
      borderRadius: '999px',
      content: '""',
      height: '12px',
      width: '12px',
    },
  }),
  readonlyDetails: css({
    display: 'grid',
    gap: '6px 10px',
    gridTemplateColumns: '86px minmax(0, 1fr)',
    margin: 0,
    '& dt': {
      color: 'var(--streamlain-v2-text-secondary)',
      fontSize: '12px',
    },
    '& dd': {
      color: 'var(--streamlain-v2-text-primary)',
      fontSize: '12px',
      margin: 0,
      minWidth: 0,
      overflowWrap: 'anywhere',
    },
  }),
  root: css({
    boxSizing: 'border-box',
    color: 'var(--streamlain-v2-text-primary)',
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    gap: '10px',
    minHeight: 0,
    overflow: 'visible',
    width: '100%',
  }),
  resizeHandle: css({
    background: 'transparent',
    border: 'none',
    cursor: 'ns-resize',
    height: '6px',
    left: 0,
    padding: 0,
    position: 'absolute',
    right: 0,
    touchAction: 'none',
    zIndex: 4,
    '&[data-edge="start"]': {
      cursor: 'n-resize',
      top: 0,
    },
    '&[data-edge="end"]': {
      bottom: 0,
      cursor: 's-resize',
    },
    ':hover': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-brand-primary) 28%, transparent)',
    },
  }),
  dayResizeHandle: css({
    background: 'transparent',
    border: 'none',
    bottom: 0,
    cursor: 'ew-resize',
    padding: 0,
    position: 'absolute',
    top: 0,
    touchAction: 'none',
    width: '8px',
    zIndex: 4,
    '&[data-edge="start"]': {
      cursor: 'w-resize',
      left: 0,
    },
    '&[data-edge="end"]': {
      cursor: 'e-resize',
      right: 0,
    },
    ':hover': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-brand-primary) 28%, transparent)',
    },
  }),
  daySlotPreview: css({
    background:
      'color-mix(in srgb, var(--streamlain-v2-brand-primary) 16%, var(--streamlain-v2-layer-background-primary))',
    border: '1px solid var(--streamlain-v2-brand-primary)',
    borderRadius: '5px',
    height: '26px',
    opacity: 0.9,
    pointerEvents: 'none',
  }),
  recurrenceGrid: css({
    display: 'grid',
    gap: '8px',
    gridTemplateColumns:
      'minmax(0, 1.2fr) minmax(64px, 0.5fr) minmax(72px, 0.5fr) minmax(110px, 0.8fr)',
    '& input, & select': {
      background: 'var(--streamlain-v2-layer-background-primary)',
      border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
      borderRadius: '6px',
      color: 'var(--streamlain-v2-text-primary)',
      font: 'inherit',
      height: '30px',
      minWidth: 0,
      padding: '0 8px',
    },
    '@media (max-width: 560px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  }),
  recurrenceField: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
    '& span': {
      color: 'var(--streamlain-v2-text-secondary)',
      fontSize: '11px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
  emptyState: emptyStateStyle,
  emptyStateCta: emptyStateCtaStyle,
  emptyStateText: emptyStateHintStyle,
  emptyStateTitle: emptyStateTitleStyle,
  subscriptionChip: css({
    alignItems: 'center',
    background: 'var(--streamlain-v2-layer-background-secondary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '6px',
    color: 'var(--streamlain-v2-text-secondary)',
    display: 'inline-flex',
    fontSize: '12px',
    gap: '4px',
    minHeight: '28px',
    maxWidth: '100%',
    padding: '0 6px',
    '& label': {
      alignItems: 'center',
      display: 'inline-flex',
      gap: '6px',
      minWidth: 0,
    },
    '& span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& small': {
      color: 'var(--streamlain-v2-text-secondary)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '&[data-error="true"]': {
      borderColor: 'var(--streamlain-v2-status-error)',
    },
  }),
  allDayHeader: css({
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    border: 'none',
    borderBottom: 'none',
    borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    display: 'grid',
    gridTemplateColumns: '56px minmax(0, 1fr)',
    minHeight: '44px',
    '&[data-collapsed="true"]': {
      minHeight: '32px',
    },
  }),
  allDayLabel: css({
    alignItems: 'center',
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    color: 'var(--streamlain-v2-text-secondary)',
    display: 'flex',
    fontSize: '11px',
    height: '44px',
    justifyContent: 'center',
    padding: 0,
    left: 0,
    position: 'sticky',
    zIndex: 3,
    ...calendarStickyColumnSolidEdge,
    '&[data-collapsed="true"]': {
      height: '32px',
    },
  }),
  allDayToggle: css({
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    gap: '5px',
    minHeight: '24px',
    minWidth: '42px',
    padding: '0 4px',
    justifyContent: 'center',
    ':hover': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
  }),
  timedOffscreenIndicator: css({
    alignItems: 'center',
    appearance: 'none',
    backdropFilter: 'blur(8px)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '999px',
    boxShadow: 'var(--streamlain-v2-shadow-level2)',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: '12px',
    fontWeight: 600,
    gap: '4px',
    left: 'calc(56px + (100% - 56px) / 2)',
    minHeight: '28px',
    padding: '0 10px',
    pointerEvents: 'auto',
    position: 'absolute',
    transform: 'translateX(-50%)',
    userSelect: 'none',
    width: 'max-content',
    zIndex: 20,
    '&&&': {
      background:
        'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 85%, transparent)',
      color: 'var(--streamlain-v2-text-primary)',
    },
    '&[data-calendar-offscreen="top"]': {
      top: '8px',
    },
    '&[data-calendar-offscreen="bottom"]': {
      bottom: '8px',
    },
    '&:hover': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
    '& span': {
      fontSize: '14px',
      lineHeight: 1,
    },
    '& strong': {
      minWidth: '14px',
      textAlign: 'center',
    },
  }),
  allDayCollapseIcon: css({
    borderBottom: '1.5px solid currentColor',
    borderRight: '1.5px solid currentColor',
    display: 'inline-block',
    height: '6px',
    transform: 'rotate(45deg) translateY(-1px)',
    width: '6px',
    '&[data-collapsed="true"]': {
      transform: 'rotate(-45deg)',
    },
  }),
  allDayBand: css({
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    display: 'grid',
    gap: '4px',
    gridTemplateColumns:
      'repeat(var(--calendar-day-count), minmax(var(--calendar-day-width, 0px), 1fr))',
    minHeight: '44px',
    minWidth: 0,
    padding: '4px',
    position: 'relative',
    touchAction: 'none',
    '&[data-collapsed="true"]': {
      minHeight: '32px',
      padding: 0,
    },
    '&[data-drop-active="true"]': {
      backgroundColor:
        'color-mix(in srgb, var(--streamlain-v2-brand-primary) 8%, var(--streamlain-v2-layer-background-primary))',
      outline:
        '1px dashed color-mix(in srgb, var(--streamlain-v2-brand-primary) 55%, transparent)',
      outlineOffset: '-3px',
    },
  }),
  daySeparatorGrid: css({
    display: 'grid',
    gridTemplateColumns:
      'repeat(var(--calendar-day-count), minmax(var(--calendar-day-width, 0px), 1fr))',
    inset: 0,
    pointerEvents: 'none',
    position: 'absolute',
    '& > i': {
      borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
      boxSizing: 'border-box',
    },
  }),
  currentTimeLine: css({
    background: 'var(--streamlain-v2-status-error)',
    height: '2px',
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: `calc(var(--calendar-current-minute) * (${CALENDAR_WEEK_HOUR_HEIGHT}px / 60))`,
    zIndex: 3,
  }),
  hourGrid: css({
    backgroundImage:
      'linear-gradient(to bottom, transparent calc(100% - 1px), var(--streamlain-v2-layer-insideBorder-border) calc(100% - 1px)), linear-gradient(to bottom, transparent calc(50% - 0.5px), color-mix(in srgb, var(--streamlain-v2-layer-insideBorder-border) 48%, transparent) 50%, transparent calc(50% + 0.5px))',
    backgroundSize: `100% ${CALENDAR_WEEK_HOUR_HEIGHT}px`,
    cursor: 'crosshair',
    minHeight: `${CALENDAR_WEEK_HOUR_GRID_HEIGHT}px`,
    position: 'relative',
    touchAction: 'none',
    ':hover': {
      backgroundColor:
        'color-mix(in srgb, var(--streamlain-v2-brand-primary) 3%, transparent)',
    },
  }),
  hourSlot: css({
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    cursor: 'crosshair',
    display: 'block',
    height: `${CALENDAR_WEEK_HOUR_HEIGHT}px`,
    padding: 0,
    width: '100%',
    ':hover': {
      background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    },
  }),
  timeAxis: css({
    background:
      'var(--streamlain-calendar-sticky-surface-background, var(--streamlain-v2-layer-background-primary))',
    borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    minWidth: '56px',
    left: 0,
    position: 'sticky',
    zIndex: 11,
    ...calendarStickyColumnSolidEdge,
  }),
  timeAxisHour: css({
    color: 'var(--streamlain-v2-text-secondary)',
    fontSize: '11px',
    height: `${CALENDAR_WEEK_HOUR_HEIGHT}px`,
    lineHeight: '16px',
    padding: '2px 8px 0 0',
    textAlign: 'right',
  }),
  timeGrid: css({
    border: 'none',
    borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: 0,
    display: 'grid',
    flex: '1 1 auto',
    gridTemplateColumns: '56px minmax(0, 1fr)',
    minHeight: 0,
    minWidth: 0,
    overflow: 'auto',
    overscrollBehaviorY: 'none',
    position: 'relative',
    ...slimDataViewScrollbarStyle,
    '&[data-external-scroll="true"]': {
      overflow: 'visible',
    },
  }),
  timeGridFrame: css({
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    minWidth: 0,
    position: 'relative',
    width: '100%',
  }),
  timeGridHeader: css({
    background:
      'var(--streamlain-calendar-header-background, var(--streamlain-v2-layer-background-primary))',
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    minWidth: 0,
    // Day headers and the all-day lane stay pinned while the hours scroll.
    // Day mode scrolls the hour grid below this header; week mode scrolls the
    // complete surface, so the header sticks to that scroller. The lane stays
    // in normal layout (never an overlay) and keeps its measured height; its
    // render limit bounds how tall it can get. The z-index keeps translated
    // drag ghosts below the header without lowering them behind other events.
    position: 'sticky',
    top: 0,
    zIndex: 30,
  }),
  timeGridContent: css({
    minWidth: 0,
  }),
  timeGridDay: css({
    borderRight: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    boxSizing: 'border-box',
    minWidth: 0,
  }),
  timeGridDays: css({
    display: 'grid',
    gridTemplateColumns:
      'repeat(var(--calendar-day-count), minmax(var(--calendar-day-width, 0px), 1fr))',
  }),
  slotPreview: css({
    alignItems: 'flex-start',
    background:
      'color-mix(in srgb, var(--streamlain-v2-brand-primary) 12%, transparent)',
    border:
      '1px dashed color-mix(in srgb, var(--streamlain-v2-brand-primary) 70%, transparent)',
    borderRadius: '5px',
    color: 'var(--streamlain-v2-brand-primary)',
    display: 'flex',
    fontSize: '11px',
    fontWeight: 600,
    height: `max(24px, calc(var(--calendar-slot-duration-minute) * (${CALENDAR_WEEK_HOUR_HEIGHT}px / 60) - 2px))`,
    left: '4px',
    padding: '4px 6px',
    pointerEvents: 'none',
    position: 'absolute',
    right: '4px',
    top: `calc(var(--calendar-slot-start-minute) * (${CALENDAR_WEEK_HOUR_HEIGHT}px / 60))`,
    zIndex: 6,
  }),
  timedEvent: css({
    alignItems: 'start',
    background:
      'color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 14%, var(--streamlain-v2-layer-background-primary))',
    border:
      '1px solid color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 42%, transparent)',
    borderLeft:
      '3px solid var(--calendar-event-color, var(--streamlain-v2-brand-primary))',
    borderRadius: '5px',
    color: 'var(--streamlain-v2-text-primary)',
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    left: 'calc(4px + ((100% - 8px) / var(--calendar-event-columns)) * var(--calendar-event-column))',
    minHeight: '24px',
    overflow: 'hidden',
    padding: '4px 6px',
    position: 'absolute',
    right: 'auto',
    textAlign: 'left',
    top: `calc(var(--calendar-event-start-minute) * (${CALENDAR_WEEK_HOUR_HEIGHT}px / 60))`,
    height: `max(24px, calc(var(--calendar-event-duration-minute) * (${CALENDAR_WEEK_HOUR_HEIGHT}px / 60) - 2px))`,
    width: 'calc((100% - 8px) / var(--calendar-event-columns) - 2px)',
    zIndex: 2,
    ':active': {
      cursor: 'grabbing',
    },
    '&[data-selected="true"]': {
      borderColor: 'var(--streamlain-v2-brand-primary)',
      boxShadow: '0 0 0 1px var(--streamlain-v2-brand-primary)',
    },
    '&[data-dragging="true"]': {
      boxShadow: '0 10px 20px rgba(15, 23, 42, 0.18)',
      cursor: 'grabbing',
      opacity: 0.94,
      pointerEvents: 'none',
      zIndex: 8,
    },
    '&[data-calendar-drag-placeholder="true"]': {
      opacity: 0.34,
      pointerEvents: 'none',
    },
    '&[data-calendar-drag-ghost="true"]': {
      boxShadow: '0 14px 28px rgba(15, 23, 42, 0.24)',
      opacity: 0.96,
      pointerEvents: 'none',
      zIndex: 24,
    },
    '&[data-calendar-drag-ghost="true"][data-calendar-drag-floating="true"]': {
      transform:
        'translate(var(--calendar-drag-x, 0px), var(--calendar-drag-y, 0px)) scale(1.01)',
      willChange: 'transform',
    },
    '&[data-invalid="true"]': {
      borderColor: 'var(--streamlain-v2-status-error)',
      borderLeftColor: 'var(--streamlain-v2-status-error)',
    },
    '&[data-invitation-preview="true"]': {
      background:
        'color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 6%, var(--streamlain-v2-layer-background-primary))',
      borderColor:
        'color-mix(in srgb, var(--calendar-event-color, var(--streamlain-v2-brand-primary)) 48%, var(--streamlain-v2-layer-insideBorder-border))',
      borderStyle: 'dashed',
      borderLeftStyle: 'solid',
      opacity: 0.78,
    },
    '&[data-selected="true"][data-invitation-preview="true"]': {
      borderColor: 'var(--streamlain-v2-brand-primary)',
      boxShadow: 'none',
      opacity: 0.9,
    },
    '&[data-calendar-event-density="compact"], &[data-calendar-event-density="micro"]':
      {
        gap: 0,
        justifyContent: 'center',
        padding: '2px 4px',
      },
    '&[data-calendar-event-density="compact"] [data-calendar-event-primary], &[data-calendar-event-density="micro"] [data-calendar-event-primary]':
      {
        alignItems: 'baseline',
        flexDirection: 'row',
        gap: '4px',
      },
    '&[data-calendar-event-density="compact"] [data-calendar-event-metadata], &[data-calendar-event-density="micro"] [data-calendar-event-metadata]':
      {
        display: 'none',
      },
    '&[data-calendar-event-density="compact"] [data-calendar-event-badge], &[data-calendar-event-density="micro"] [data-calendar-event-badge]':
      {
        display: 'none',
      },
    '&[data-calendar-event-density="micro"] [data-calendar-event-primary] > span:first-child':
      {
        flex: '0 0 auto',
        fontSize: '10px',
      },
    '&[data-readonly="true"]:not([data-invitation-preview="true"])': {
      cursor: 'pointer',
    },
    '& span, & small': {
      color: 'var(--streamlain-v2-text-secondary)',
      fontSize: '11px',
      lineHeight: 1.2,
    },
    '& strong': {
      color: 'var(--streamlain-v2-text-primary)',
      fontSize: '12px',
      lineHeight: 1.2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    },
  }),
  timedEventPrimary: css({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
  }),
  todayButton: temporalToolbarButtonStyle,
  chromeHost: css({
    width: '100%',
    minWidth: 0,
    containerType: 'inline-size',
    containerName: 'calendar-chrome',
    position: 'relative',
    zIndex: 20,
  }),
  toolbar: css({
    alignItems: 'center',
    background: 'var(--streamlain-v2-layer-background-primary)',
    borderBottom: '0.5px solid var(--streamlain-v2-layer-insideBorder-border)',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)',
    gridTemplateAreas: '"leading pager actions"',
    gap: '8px',
    height: `${STREAMLAIN_APP_TOPBAR_HEIGHT}px`,
    minWidth: 0,
    padding: STREAMLAIN_COMPACT_HEADER_PADDING,
    '& [data-calendar-chrome-leading]': { gridArea: 'leading', minWidth: 0 },
    '& [data-calendar-chrome-pager]': {
      gridArea: 'pager',
      flex: 'none',
      flexWrap: 'nowrap',
      justifyContent: 'center',
    },
    '& [data-calendar-chrome-actions]': {
      gridArea: 'actions',
      justifySelf: 'end',
      flexWrap: 'nowrap',
    },
    '& [data-calendar-full-width-toggle]': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
      borderRadius: '6px',
      color: 'var(--streamlain-v2-text-secondary)',
      width: '30px',
      height: '30px',
      padding: 0,
      flex: 'none',
      cursor: 'pointer',
    },
    '& [data-temporal-range-part="title"]': {
      minWidth: 0,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      textAlign: 'start',
    },
    '@container calendar-chrome (max-width: 1000px)': {
      '& [data-temporal-range-part="title"]': { display: 'none' },
    },
    '@container calendar-chrome (max-width: 800px)': {
      '& [data-calendar-chrome-actions] [data-calendar-action-label]': {
        display: 'none',
      },
      '& [data-calendar-chrome-actions] button, & [data-calendar-chrome-actions] summary':
        { padding: '0 6px' },
      // Menu labels remain readable even when toolbar actions use icons.
      '& [role="menu"] [data-calendar-action-label]': { display: 'inline' },
    },
    '@container calendar-chrome (max-width: 520px)': {
      gridTemplateColumns: 'minmax(0,1fr) auto',
      gridTemplateAreas: '"leading actions" "pager pager"',
      height: 'auto',
      minHeight: `${STREAMLAIN_APP_TOPBAR_HEIGHT}px`,
      rowGap: '6px',
      '& [data-calendar-chrome-actions]': { justifySelf: 'end' },
      '& [data-calendar-chrome-pager]': { justifySelf: 'center' },
    },
    '@container calendar-chrome (max-width: 340px)': {
      gridTemplateColumns: 'minmax(0,1fr)',
      gridTemplateAreas: '"leading" "pager" "actions"',
    },
  }),
  weekCell: css({
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '6px',
    minHeight: '360px',
    minWidth: 0,
  }),
  weekGrid: css({
    display: 'grid',
    gap: '6px',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  }),
  weekSurface: css({
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    minHeight: `${CALENDAR_WEEK_SURFACE_MIN_HEIGHT}px`,
    minWidth: 0,
  }),
  weekScroll: css({
    flex: '0 0 auto',
    minHeight: 0,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    overscrollBehaviorX: 'none',
    overscrollBehaviorY: 'none',
    position: 'relative',
    // The week canvas sizes its days from this width (cqw).
    containerType: 'inline-size',
    ...slimDataViewScrollbarStyle,
  }),
  weekCanvas: css({
    // A week fills the scroller; calendarSurfaceWeekDayWidth applies the same rule in script.
    '--calendar-day-width': `max(${CALENDAR_SURFACE_WEEK_DAY_MIN_WIDTH}px, calc((100cqw - ${CALENDAR_SURFACE_TIME_AXIS_WIDTH}px) / 7))`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: `${CALENDAR_WEEK_SURFACE_MIN_HEIGHT}px`,
    minWidth:
      'calc(56px + var(--calendar-day-count, 7) * var(--calendar-day-width))',
  }),
  weekNumberPill: css({
    background: 'var(--streamlain-v2-layer-background-secondary)',
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    borderRadius: '999px',
    bottom: '4px',
    color: 'var(--streamlain-v2-text-secondary)',
    fontSize: '10px',
    fontWeight: 600,
    lineHeight: '14px',
    padding: '0 5px',
    position: 'absolute',
    right: '6px',
  }),
};
